/* Registering a framework: presets, identity, and the controls with their evidence.
 *
 * Built to the reference. The ordering is the argument: presets first, because most tenants are
 * registering a standard somebody else wrote and typing out GDPR's Article 30 by hand is not the
 * intended path; then the identity; then the controls, each with the evidence it draws on.
 *
 * **Every refusal this form shows is one the server already enforces.** `Control.__post_init__`
 * rejects a control naming no evidence kind — *"a control nothing can substantiate would read as
 * satisfied in every pack"* — `Framework.__post_init__` rejects an empty control list and duplicate
 * control codes, and a `(code, version)` that exists with a different definition is a 409 because
 * packs embed it. Checking here tells a reader before they submit; the server stays the authority.
 *
 * **The evidence kinds are the six the platform can collect, and there is nothing else to pick.** They
 * are rendered as toggles rather than as free text for that reason: a control demanding evidence no
 * collector produces is refused at registration, because it would read as satisfied-by-absence in
 * every pack — so the vocabulary is closed at the input rather than validated after it.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { EVIDENCE_KINDS } from '@/shared/api/generated/compliance';
import type { Framework } from '@/shared/api/generated/compliance';
import { Alert } from '@/shared/ui/Alert';

import { useRegisterFramework } from '../api/compliance';
import {
  BLANK_CONTROL,
  EMPTY_FRAMEWORK_DRAFT,
  FRAMEWORK_PRESETS,
  frameworkDraftIsValid,
  frameworkHintKey,
  duplicateControlCodes,
  presetIsChosen,
  presetIsTaken,
  toggleEvidence,
} from '../model/registration';
import type {
  ControlDraft,
  FrameworkDraft,
  FrameworkPreset,
} from '../model/registration';

/** Turn a preset into a draft. Every field lands editable — that is the whole point of a preset. */
function draftFromPreset(
  preset: FrameworkPreset,
  t: (key: string) => string,
): FrameworkDraft {
  return {
    code: preset.code,
    version: preset.version,
    title: t(preset.titleKey),
    description: t(preset.descriptionKey),
    controls: preset.controls.map((control) => ({
      code: control.code,
      title: t(control.titleKey),
      evidence: [...control.evidence],
    })),
  };
}

function PresetCard({
  preset,
  existing,
  draft,
  onApply,
}: {
  readonly preset: FrameworkPreset;
  readonly existing: readonly Framework[];
  readonly draft: FrameworkDraft;
  readonly onApply: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const taken = presetIsTaken(preset, existing);
  const chosen = presetIsChosen(preset, draft);

  const classes = ['lq-cp__preset'];
  if (chosen) classes.push('lq-cp__preset--chosen');
  if (taken) classes.push('lq-cp__preset--taken');

  return (
    <button
      type="button"
      className={classes.join(' ')}
      disabled={taken}
      title={
        taken
          ? t('compliance.preset.takenWhy', {
              code: preset.code,
              version: preset.version,
            })
          : t('compliance.preset.applyWhy', {
              count: preset.controls.length,
              title: t(preset.titleKey),
            })
      }
      onClick={onApply}
    >
      <div className="lq-cp__presetCode">
        {preset.code}{' '}
        <span className="lq-cp__presetVersion lq-mono">{preset.version}</span>
      </div>
      {/* An already-registered preset says so where its region would go: the reason it is disabled
          is more useful than the fact it is Indian. */}
      <div className="lq-cp__presetNote">
        {taken
          ? t('compliance.preset.taken')
          : t('compliance.preset.note', {
              region: t(preset.regionKey),
              count: preset.controls.length,
            })}
      </div>
    </button>
  );
}

function ControlEditor({
  control,
  index,
  removable,
  onPatch,
  onRemove,
}: {
  readonly control: ControlDraft;
  readonly index: number;
  readonly removable: boolean;
  readonly onPatch: (patch: Partial<ControlDraft>) => void;
  readonly onRemove: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const codeId = `lq-cp-ctl-code-${String(index)}`;
  const titleId = `lq-cp-ctl-title-${String(index)}`;

  return (
    <div className="lq-cp__control">
      <div className="lq-cp__controlGrid">
        <div className="lq-inlineform__field">
          <label className="lq-inlineform__label" htmlFor={codeId}>
            {t('compliance.reg.controlCode')}
          </label>
          <input
            id={codeId}
            className="lq-mono"
            type="text"
            value={control.code}
            placeholder={t('compliance.reg.controlCodePlaceholder')}
            onChange={(event) => {
              onPatch({ code: event.target.value });
            }}
          />
        </div>
        <div className="lq-inlineform__field">
          <label className="lq-inlineform__label" htmlFor={titleId}>
            {t('compliance.reg.controlTitle')}
          </label>
          <input
            id={titleId}
            type="text"
            value={control.title}
            placeholder={t('compliance.reg.controlTitlePlaceholder')}
            onChange={(event) => {
              onPatch({ title: event.target.value });
            }}
          />
        </div>
        {/* The first control is not removable: a framework with none generates an empty pack that
            reads as a clean audit, which the domain refuses outright. */}
        {removable && (
          <button type="button" className="lq-cp__removeControl" onClick={onRemove}>
            {t('compliance.reg.removeControl')}
          </button>
        )}
      </div>

      <div className="lq-cp__evidenceLabel">
        {t('compliance.reg.evidenceLabel')}{' '}
        <span className="lq-cp__evidenceAside">{t('compliance.reg.evidenceAside')}</span>
      </div>
      <div className="lq-cp__kinds">
        {EVIDENCE_KINDS.map((kind) => {
          const on = control.evidence.includes(kind);
          return (
            <button
              key={kind}
              type="button"
              className={
                on ? 'lq-cp__toggle lq-cp__toggle--on lq-mono' : 'lq-cp__toggle lq-mono'
              }
              aria-pressed={on}
              title={t(`compliance.evidence.${kind}`)}
              onClick={() => {
                onPatch({ evidence: toggleEvidence(control, kind).evidence });
              }}
            >
              {kind}
            </button>
          );
        })}
      </div>

      {/* Warned in place rather than only in the footer hint, because on a five-control framework the
          footer names the problem and not which control has it. */}
      {control.evidence.length === 0 && (
        <div className="lq-cp__controlWarn">{t('compliance.reg.noEvidence')}</div>
      )}
    </div>
  );
}

export function RegisterFramework({
  existing,
  onClose,
  onRegistered,
}: {
  readonly existing: readonly Framework[];
  readonly onClose: () => void;
  readonly onRegistered: (framework: Framework) => void;
}): ReactElement {
  const { t } = useTranslation();
  const register = useRegisterFramework();
  const [draft, setDraft] = useState<FrameworkDraft>(EMPTY_FRAMEWORK_DRAFT);
  const valid = frameworkDraftIsValid(draft, existing);
  const repeated = duplicateControlCodes(draft);

  const patchControl = (index: number, patch: Partial<ControlDraft>): void => {
    setDraft((current) => ({
      ...current,
      controls: current.controls.map((control, at) =>
        at === index ? { ...control, ...patch } : control,
      ),
    }));
  };

  return (
    <div className="lq-inlineform lq-cp__reg">
      <div className="lq-inlineform__title">{t('compliance.reg.title')}</div>
      <p className="lq-inlineform__intro">{t('compliance.reg.intro')}</p>

      <div className="lq-cp__presets">
        <div className="lq-cp__presetsTitle">{t('compliance.preset.heading')}</div>
        <p className="lq-cp__presetsIntro">{t('compliance.preset.intro')}</p>
        <div className="lq-cp__presetRow">
          {FRAMEWORK_PRESETS.map((preset) => (
            <PresetCard
              key={`${preset.code}@${preset.version}`}
              preset={preset}
              existing={existing}
              draft={draft}
              onApply={() => {
                setDraft(draftFromPreset(preset, t));
              }}
            />
          ))}
          {/* Dashed rather than solid: this is the absence of a preset, not a fifth one. */}
          <button
            type="button"
            className="lq-cp__preset lq-cp__preset--blank"
            onClick={() => {
              setDraft(EMPTY_FRAMEWORK_DRAFT);
            }}
          >
            <div className="lq-cp__presetCode">{t('compliance.preset.blank')}</div>
            <div className="lq-cp__presetNote">{t('compliance.preset.blankNote')}</div>
          </button>
        </div>
      </div>

      <div className="lq-cp__idGrid">
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('compliance.reg.code')}</span>
          <input
            className="lq-mono"
            type="text"
            value={draft.code}
            placeholder={t('compliance.reg.codePlaceholder')}
            onChange={(event) => {
              setDraft((current) => ({ ...current, code: event.target.value }));
            }}
          />
        </label>
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">{t('compliance.reg.version')}</span>
          <input
            className="lq-mono"
            type="text"
            value={draft.version}
            placeholder={t('compliance.reg.versionPlaceholder')}
            onChange={(event) => {
              setDraft((current) => ({ ...current, version: event.target.value }));
            }}
          />
        </label>
        <label className="lq-inlineform__field">
          <span className="lq-inlineform__label">
            {t('compliance.reg.frameworkTitle')}
          </span>
          <input
            type="text"
            value={draft.title}
            placeholder={t('compliance.reg.titlePlaceholder')}
            onChange={(event) => {
              setDraft((current) => ({ ...current, title: event.target.value }));
            }}
          />
        </label>
      </div>

      <label className="lq-inlineform__field lq-cp__descField">
        <span className="lq-inlineform__label">{t('compliance.reg.description')}</span>
        <input
          type="text"
          value={draft.description}
          placeholder={t('compliance.reg.descriptionPlaceholder')}
          onChange={(event) => {
            setDraft((current) => ({ ...current, description: event.target.value }));
          }}
        />
      </label>

      <div className="lq-cp__controlsHead">{t('compliance.reg.controls')}</div>
      {draft.controls.map((control, index) => (
        <ControlEditor
          /* Keyed on the index, deliberately. A draft control has no identity: its code is the
             field being typed, so keying on that would remount the input on every keystroke and
             lose focus mid-word. The position is the row's identity while it is being authored. */
          key={index}
          control={control}
          index={index}
          removable={draft.controls.length > 1}
          onPatch={(patch) => {
            patchControl(index, patch);
          }}
          onRemove={() => {
            setDraft((current) => ({
              ...current,
              controls: current.controls.filter((_, at) => at !== index),
            }));
          }}
        />
      ))}

      <button
        type="button"
        className="lq-cp__addControl"
        onClick={() => {
          setDraft((current) => ({
            ...current,
            controls: [...current.controls, BLANK_CONTROL],
          }));
        }}
      >
        {t('compliance.reg.addControl')}
      </button>

      {register.isError && (
        /* The server's own words. A 409 explains that a version is immutable because packs embed it,
           and restating that here would be a second place deciding what it means. */
        <Alert tone="error" title={t('compliance.reg.failed')}>
          {register.error.message}
        </Alert>
      )}

      <div className="lq-inlineform__foot">
        <div className={valid ? 'lq-inlineform__summary' : 'lq-inlineform__blocked'}>
          {t(frameworkHintKey(draft, existing), {
            code: draft.code.trim(),
            version: draft.version.trim(),
            count: draft.controls.length,
            codes: repeated.join(', '),
          })}
        </div>
        <button type="button" className="lq-inlineform__cancel" onClick={onClose}>
          {t('compliance.reg.cancel')}
        </button>
        <button
          type="button"
          className="lq-inlineform__submit"
          disabled={!valid || register.isPending}
          onClick={() => {
            if (!valid) return;
            register.mutate(
              {
                code: draft.code.trim(),
                version: draft.version.trim(),
                title: draft.title.trim(),
                description: draft.description.trim(),
                controls: draft.controls.map((control) => ({
                  code: control.code.trim(),
                  title: control.title.trim(),
                  evidence: [...control.evidence],
                })),
              },
              {
                onSuccess: (framework) => {
                  setDraft(EMPTY_FRAMEWORK_DRAFT);
                  onRegistered(framework);
                },
              },
            );
          }}
        >
          {t('compliance.reg.submit')}
        </button>
      </div>
    </div>
  );
}
